import React, {
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { QButton } from "./QButton";

// The dialog itself (Radix + verbatim values copy) is a lazy chunk — see
// CoreValuesDialog.jsx. It is fetched the first time the modal opens and
// stays mounted afterwards so the close animation still plays.
const CoreValuesDialog = React.lazy(() => import("./CoreValuesDialog"));

/* ---------- Context + provider ---------- */
const CoreValuesCtx = createContext({ open: () => {}, close: () => {} });

export const useCoreValues = () => useContext(CoreValuesCtx);

export function CoreValuesProvider({ children }) {
  const [params, setParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);
  const navigate = useNavigate();

  // URL-driven open: ?modal=values
  useEffect(() => {
    if (params.get("modal") === "values") {
      setIsOpen(true);
    }
  }, [params]);

  useEffect(() => {
    if (isOpen) setEverOpened(true);
  }, [isOpen]);

  const open = useCallback(() => {
    setIsOpen(true);
    const next = new URLSearchParams(params);
    next.set("modal", "values");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const close = useCallback(() => {
    setIsOpen(false);
    if (params.get("modal") === "values") {
      const next = new URLSearchParams(params);
      next.delete("modal");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const handleCta = useCallback(
    (path) => {
      setIsOpen(false);
      const next = new URLSearchParams(params);
      next.delete("modal");
      setParams(next, { replace: true });
      // small delay so the closing animation can begin before route change
      setTimeout(() => navigate(path), 60);
    },
    [params, setParams, navigate]
  );

  const ctx = useMemo(() => ({ open, close }), [open, close]);

  return (
    <CoreValuesCtx.Provider value={ctx}>
      {children}
      {everOpened && (
        <Suspense fallback={null}>
          <CoreValuesDialog isOpen={isOpen} onClose={close} onCta={handleCta} />
        </Suspense>
      )}
    </CoreValuesCtx.Provider>
  );
}

/* ---------- Reusable trigger button ---------- */
export function CoreValuesTrigger({
  children = "See our core values",
  className = "",
  variant = "ghost",
  size = "sm",
  testId = "core-values-trigger",
  iconRight,
}) {
  const { open } = useCoreValues();
  return (
    <QButton
      type="button"
      variant={variant}
      size={size}
      onClick={open}
      className={className}
      data-testid={testId}
      iconRight={iconRight}
    >
      {children}
    </QButton>
  );
}

export default CoreValuesProvider;
